import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useItemValidator } from "../../hooks/useItemValidator";
import { parseNeoboardsSource } from "../../utils/neoboardsSourceParser";

const downloadText = (filename, content) => {
  if (!content) return;

  const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatTradesForPaste = (trades) =>
  trades
    .map(
      (trade) =>
        `${trade.trade_date}\nsent: ${trade.items_sent}\nreceived: ${trade.items_received}\nnotes: ${trade.notes}`
    )
    .join("\n\n");

const SummaryCard = ({ label, value }) => (
  <Paper
    elevation={2}
    sx={{
      minWidth: 150,
      px: 2,
      py: 1.5,
      textAlign: "center",
    }}
  >
    <Typography variant="overline" sx={{ display: "block" }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
      {value}
    </Typography>
  </Paper>
);

const AdminNeoboardsSourceParser = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { validate, loading } = useItemValidator();

  const [sourceHtml, setSourceHtml] = useState("");
  const [parseError, setParseError] = useState("");
  const [parseResult, setParseResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState("");

  const markCopied = (key) => {
    setCopiedKey(key);
    window.setTimeout(() => {
      setCopiedKey((currentKey) => (currentKey === key ? "" : currentKey));
    }, 1600);
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      markCopied(key);
    } catch (err) {
      console.error(err);
    }
  };

  const validGroups = useMemo(() => {
    if (!parseResult) return [];

    const grouped = new Map();

    parseResult.validTrades.forEach((trade) => {
      const existing = grouped.get(trade.username) || [];
      existing.push(trade);
      grouped.set(trade.username, existing);
    });

    return Array.from(grouped.entries()).map(([username, trades]) => ({
      username,
      trades,
      pasteText: formatTradesForPaste(trades),
      copyBundle: `USERNAME: ${username}\nREMOVE THIS LINE AFTER COPYING THE USERNAME INTO PASTE TRADES.\n\n${formatTradesForPaste(
        trades
      )}`,
    }));
  }, [parseResult]);

  const skippedPostRows = useMemo(() => parseResult?.skippedPosts || [], [parseResult]);

  const handleParse = () => {
    setParseError("");

    if (!sourceHtml.trim()) {
      setParseResult(null);
      setParseError("Paste Neoboards page source HTML before parsing.");
      return;
    }

    try {
      const result = parseNeoboardsSource(sourceHtml, validate);
      setParseResult(result);
      setSourceHtml("");
    } catch (err) {
      console.error(err);
      setParseResult(null);
      setParseError(err.message || "Failed to parse Neoboards source.");
    }
  };

  const handleClear = () => {
    setSourceHtml("");
    setParseError("");
    setParseResult(null);
  };

  return (
    <Box sx={{ px: 2, py: 4 }}>
      <Typography
        variant="h5"
        sx={{
          color: theme.palette.accent.main,
          fontWeight: "bold",
          textAlign: "center",
          mb: 3,
        }}
      >
        admin: neoboards source parser
      </Typography>

      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRadius: 2,
          p: 4,
          boxShadow: 3,
        }}
      >
        <Stack spacing={3}>
          <Alert severity="info">
            Paste raw Neoboards topic HTML here. Valid trades are grouped into paste-ready
            blocks for <strong>paste trades</strong>, while anything malformed stays in a
            separate review export with source post metadata.
          </Alert>

          {loading && (
            <Alert severity="warning">
              Item names are still loading, so trade validation is not ready yet.
            </Alert>
          )}

          {parseError && <Alert severity="error">{parseError}</Alert>}

          <TextField
            label="Neoboards page source HTML"
            value={sourceHtml}
            onChange={(event) => setSourceHtml(event.target.value)}
            fullWidth
            multiline
            minRows={18}
            placeholder="Paste the board page HTML here..."
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "0.72rem",
                lineHeight: 1.35,
              },
            }}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="contained"
              onClick={handleParse}
              disabled={loading || !sourceHtml.trim()}
            >
              Parse Source
            </Button>
            <Button variant="outlined" onClick={handleClear}>
              Clear
            </Button>
            <Button variant="outlined" onClick={() => navigate("/admin/pasteTrades")}>
              Go To Paste Trades
            </Button>
          </Stack>

          {parseResult && (
            <>
              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <SummaryCard label="Posts Found" value={parseResult.summary.totalPosts} />
                <SummaryCard
                  label="Posts With Trades"
                  value={parseResult.summary.postsWithTrades}
                />
                <SummaryCard label="Trades Parsed" value={parseResult.summary.totalTrades} />
                <SummaryCard label="Valid Trades" value={parseResult.summary.validTrades} />
                <SummaryCard label="Needs Review" value={parseResult.summary.reviewTrades} />
                <SummaryCard label="Skipped Posts" value={parseResult.summary.skippedPosts} />
              </Stack>

              {parseResult.summary.validTrades > 0 && (
                <Alert severity="success">
                  {parseResult.summary.validTrades} trades are ready to copy into
                  <strong> paste trades</strong>.
                </Alert>
              )}

              {parseResult.summary.reviewTrades > 0 && (
                <Alert severity="warning">
                  {parseResult.summary.reviewTrades} trades need manual review before import.
                </Alert>
              )}

              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Paste Trades Blocks
                </Typography>
                <Stack spacing={2}>
                  {validGroups.length === 0 && (
                    <Paper sx={{ p: 2 }}>
                      <Typography>No valid trades parsed yet.</Typography>
                    </Paper>
                  )}
                  {validGroups.map((group) => (
                    <Paper key={group.username} sx={{ p: 2 }}>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                        sx={{ mb: 2 }}
                      >
                        <Box>
                          <Typography variant="h6">{group.username}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {group.trades.length} valid trade{group.trades.length === 1 ? "" : "s"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Copy once, use the first line for the username field, then remove the
                            reminder lines before pasting the trade block.
                          </Typography>
                        </Box>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                          <Tooltip
                            title={copiedKey === `${group.username}-block` ? "Copied!" : ""}
                            open={copiedKey === `${group.username}-block`}
                            placement="top"
                          >
                            <Button
                              variant="contained"
                              onClick={() =>
                                copyToClipboard(group.copyBundle, `${group.username}-block`)
                              }
                            >
                              Copy Paste Bundle
                            </Button>
                          </Tooltip>
                          <Button
                            variant="outlined"
                            onClick={() =>
                              downloadText(
                                `neoboards-paste-trades-${group.username}.txt`,
                                group.copyBundle
                              )
                            }
                          >
                            Download TXT
                          </Button>
                        </Stack>
                      </Stack>
                      <TextField
                        value={group.copyBundle}
                        fullWidth
                        multiline
                        minRows={Math.min(Math.max(group.trades.length * 5 + 2, 8), 20)}
                        InputProps={{ readOnly: true }}
                      />
                    </Paper>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Review Needed
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 360 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Reasons</TableCell>
                        <TableCell>Raw Block</TableCell>
                        <TableCell>Post</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parseResult.reviewTrades.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5}>No review rows right now.</TableCell>
                        </TableRow>
                      )}
                      {parseResult.reviewTrades.map((trade, index) => (
                        <TableRow key={`${trade.source_post_id}-review-${index}`}>
                          <TableCell>{trade.username}</TableCell>
                          <TableCell>{trade.trade_date || "-"}</TableCell>
                          <TableCell sx={{ minWidth: 320 }}>
                            <Stack direction="column" spacing={1}>
                              {trade.reasons.map((reason) => (
                                <Chip
                                  key={reason}
                                  label={reason}
                                  color="warning"
                                  variant="outlined"
                                  sx={{ justifyContent: "flex-start" }}
                                />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ minWidth: 360, whiteSpace: "pre-wrap" }}>
                            {trade.raw_text}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{trade.source_post_id}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {trade.source_posted_at}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Skipped Posts
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 260 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Post</TableCell>
                        <TableCell>Excerpt</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {skippedPostRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3}>No skipped posts.</TableCell>
                        </TableRow>
                      )}
                      {skippedPostRows.map((post) => (
                        <TableRow key={`${post.source_post_id}-${post.username}`}>
                          <TableCell>{post.username}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{post.source_post_id}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {post.source_posted_at}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 420 }}>{post.excerpt || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default AdminNeoboardsSourceParser;
